import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, ReactNode } from "react";
import { useAuth } from "./AuthContext";
import {
  getPlatformPreferences,
  migrateFromAcuity,
  updatePlatformPreferences,
  PlatformPreferences,
} from "../hooks/api/preferences";
import { isImpersonated } from "../hooks/api/auth/tokenManager";

const POLL_INTERVAL_MS = 10_000;

interface AcuityMigrationContextType {
  isAcuity: boolean;
  isInProgress: boolean;
  isPendingReview: boolean;
  migratingDismissed: boolean;
  doneDismissed: boolean;
  /** Alias semántico = doneDismissed. Lo usan el guard de /calendar y el review modal. */
  welcomeDismissed: boolean;
  loading: boolean;
  dismissReview: () => Promise<void>;
  dismissMigrating: () => void;
  dismissDone: () => void;
}

const AcuityMigrationContext = createContext<AcuityMigrationContextType | undefined>(undefined);

interface ProviderProps {
  children: ReactNode;
}

export const AcuityMigrationProvider: React.FC<ProviderProps> = ({ children }) => {
  const { isAuthenticated, psychologistId } = useAuth();

  const [prefs, setPrefs] = useState<PlatformPreferences | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const migrationTriggeredRef = useRef<string | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const [migratingDismissed, setMigratingDismissed] = useState<boolean>(false);
  const [doneDismissed, setDoneDismissed] = useState<boolean>(false);
  const dismissMigrating = useCallback(() => setMigratingDismissed(true), []);
  const dismissDone = useCallback(() => setDoneDismissed(true), []);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const fetchPrefs = useCallback(async () => {
    try {
      const next = await getPlatformPreferences();
      setPrefs(next);
      return next;
    } catch {
      return null;
    }
  }, []);

  // Reset al cambiar de sesión / psicóloga
  useEffect(() => {
    if (!isAuthenticated || !psychologistId) {
      stopPolling();
      setPrefs(null);
      setLoading(false);
      migrationTriggeredRef.current = null;
      setMigratingDismissed(false);
      setDoneDismissed(false);
      return;
    }

    setLoading(true);
    fetchPrefs().finally(() => setLoading(false));

    return () => {
      stopPolling();
    };
  }, [isAuthenticated, psychologistId, fetchPrefs, stopPolling]);

  // Auto-trigger migración la primera vez que detectamos is_acuity sin nada corriendo.
  // No se ejecuta en sesiones impersonadas: solo el login común de la psicóloga la dispara.
  useEffect(() => {
    if (!psychologistId || !prefs) return;
    if (migrationTriggeredRef.current === psychologistId) return;
    if (isImpersonated()) return;

    const should_trigger = prefs.is_acuity && !prefs.acuity_migration_in_progress && !prefs.acuity_schedules_pending_review;
    if (!should_trigger) return;

    migrationTriggeredRef.current = psychologistId;
    migrateFromAcuity()
      .then(() => {
        setPrefs((prev) => (prev ? { ...prev, acuity_migration_in_progress: true } : prev));
      })
      .catch(() => {
        migrationTriggeredRef.current = null;
      });
  }, [psychologistId, prefs]);

  // Polling cuando in_progress=true
  useEffect(() => {
    if (!prefs?.acuity_migration_in_progress) {
      stopPolling();
      return;
    }
    if (pollTimerRef.current) return;

    pollTimerRef.current = setInterval(() => {
      fetchPrefs();
    }, POLL_INTERVAL_MS);

    return () => stopPolling();
  }, [prefs?.acuity_migration_in_progress, fetchPrefs, stopPolling]);

  const dismissReview = useCallback(async () => {
    // No permitir dismiss bajo impersonación: solo la psico desde su login común
    // puede confirmar que vio el review de horarios migrados.
    if (isImpersonated()) return;
    await updatePlatformPreferences({ acuity_schedules_pending_review: false });
    setPrefs((prev) => (prev ? { ...prev, acuity_schedules_pending_review: false } : prev));
  }, []);

  // Bajo impersonación ocultamos toda la migración Acuity: el admin no debe
  // disparar la migración ni ver/cerrar ninguno de los modales asociados.
  const impersonating = isImpersonated();

  const value = useMemo<AcuityMigrationContextType>(() => ({
    isAcuity: !impersonating && (prefs?.is_acuity ?? false),
    isInProgress: !impersonating && (prefs?.acuity_migration_in_progress ?? false),
    isPendingReview: !impersonating && (prefs?.acuity_schedules_pending_review ?? false),
    migratingDismissed,
    doneDismissed,
    welcomeDismissed: doneDismissed,
    loading,
    dismissReview,
    dismissMigrating,
    dismissDone,
  }), [impersonating, prefs, loading, dismissReview, migratingDismissed, doneDismissed, dismissMigrating, dismissDone]);

  return <AcuityMigrationContext.Provider value={value}>{children}</AcuityMigrationContext.Provider>;
};

export const useAcuityMigration = () => {
  const ctx = useContext(AcuityMigrationContext);
  if (!ctx) throw new Error("useAcuityMigration must be used within AcuityMigrationProvider");
  return ctx;
};
